import React, { useState /*useRef*/ } from "react"
import styled from "styled-components"
import PropTypes from "prop-types"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import {
  faChevronDown,
  faChevronUp,
  faExpand,
} from "@fortawesome/free-solid-svg-icons"
import { FullScreen, useFullScreenHandle } from "react-full-screen"
import { useSelector } from "react-redux"
import { selectObservationsById } from "./slice"
import AstrositeName from "../astrosites/AstrositeName"
import Timestamp from "../../ui/Timestamp"
import PaperTitle, { TitleAction, TitleText } from "../../ui/PaperTitle"
import { isVideo, isImage, isHtml, mediaSrc } from "./media"
import { fadeIn } from "../../ui/animations"
import { selectAuth } from "../iam/slice"

const Container = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
`

const Content = styled.div`
  border-radius: ${(props) => props.theme.corners};
  display: flex;
  flex: auto;
  align-items: stretch;
  justify-content: center;
  padding: 0.5rem;
  background-color: rgba(0, 0, 0, 0.25);
  img,
  video,
  iframe {
    border: none;
    animation: 0.3s ${fadeIn} ease-out;
    object-fit: contain;
    object-position: center center;
    max-height: ${(props) =>
      props.fullscreen
        ? "calc(100vh - 2rem)"
        : props.file?.name === "events_and_tles_widget"
        ? "unset"
        : "45vh"};
  }
  & > * {
    flex-grow: 1;
  }
`

/**
 * Renders a media file from an observation
 * @param observationId
 * @param file Details about the file e.g. file type
 * @param expanded Indicates whether the section should be expanded
 * @param height
 * @param width
 * @returns {JSX.Element}
 * @constructor
 */
const ObservationMediaFile = ({
  observationId,
  file = {},
  expanded = true,
}) => {
  const [mediaExpanded, setMediaExpanded] = useState(expanded)
  const [fullscreen, setFullscreen] = useState(false)
  const onClickExpand = () => {
    setMediaExpanded(() => !mediaExpanded)
    setFullscreen(false)
  }
  const handle = useFullScreenHandle()
  const { token } = useSelector(selectAuth)

  // Double-click on media toggles fullscreen
  const handleMediaClick = (e) => {
    if (e.detail == 2) {
      if (fullscreen) {
        handle.exit()
      } else {
        handle.enter()
      }
    }
  }

  return (
    <Container>
      <PaperTitle>
        <TitleText>{file.display_name || "(unnamed)"}</TitleText>
        <TitleAction onClick={onClickExpand}>
          <FontAwesomeIcon
            icon={mediaExpanded ? faChevronUp : faChevronDown}
            title={mediaExpanded ? "Collapse" : "Expand"}
          />
        </TitleAction>
        <TitleAction onClick={handle.enter}>
          <FontAwesomeIcon icon={faExpand} title="Show full screen" />
        </TitleAction>
      </PaperTitle>
      {mediaExpanded && (
        <Content file={file} fullscreen={fullscreen}>
          <FullScreen handle={handle} onChange={setFullscreen}>
            {observationId && file && fullscreen && (
              <FileBanner observationId={observationId} file={file} />
            )}
            {isImage(file) && (
              <img
                src={mediaSrc(observationId, file.id, token)}
                key={
                  mediaSrc(
                    observationId,
                    file.id,
                    token
                  ) /* Forces not to display stale image */
                }
                width="100%"
                height="100%"
                onClick={handleMediaClick}
              />
            )}
            {isVideo(file) && (
              <video
                src={mediaSrc(observationId, file.id, token)}
                key={
                  mediaSrc(
                    observationId,
                    file.id,
                    token
                  ) /* Forces not to display stale image */
                }
                controls
                width="100%"
                height="100%"
                onClick={handleMediaClick}
              ></video>
            )}
            {isHtml(file) && (
              <iframe
                src={mediaSrc(observationId, file.id, token)}
                key={
                  mediaSrc(
                    observationId,
                    file.id,
                    token
                  ) /* Forces not to display stale image */
                }
                width="100%"
                height="100%"
                onClick={handleMediaClick}
              />
            )}
            {!isImage(file) && !isVideo(file) && !isHtml(file) && (
              <code>{file?.type}</code>
            )}
          </FullScreen>
        </Content>
      )}
    </Container>
  )
}

ObservationMediaFile.propTypes = {
  observationId: PropTypes.string,
  file: PropTypes.object, // Details about the file e.g. file type
  expanded: PropTypes.bool, // Indicates whether the section should be expanded
  height: PropTypes.number, // TODO control with CSS?
  width: PropTypes.number, // TODO control with CSS?
}

// Banner displayed while full-screen
const FullScreenBanner = styled.div`
  height: 2rem;
  padding: 0.5rem 2rem;
  background-color: ${(props) => props.theme.colours.paperSolid};
  color: ${(props) => props.theme.colours.text.primary};
  display: flex;
  align-items: center;
`
const FSName = styled.div`
  flex: 1;
  font-weight: bold;
`
const FSObsName = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${(props) => props.theme.colours.text.secondary};
`
const FSTime = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  color: ${(props) => props.theme.colours.primary};
`
/** FileBanner presents an informative full-screen banner, referencing observation
 * details to provide context. */
const FileBanner = ({ observationId, file = {} }) => {
  // Observation info - used as display context when full-screen (banner)
  const observationsByID = useSelector(selectObservationsById)
  const obs = observationsByID && observationsByID[observationId]
  const advancedMode = false // TODO: Wire up to global advanced mode
  return (
    <FullScreenBanner>
      <FSName>{file?.display_name || file?.id || "(unnamed)"}</FSName>
      <FSObsName>
        {obs?.name || "-"}
        {" - "}
        <AstrositeName astrositeId={obs?.astrosite} />
      </FSObsName>
      <FSTime>
        <Timestamp utc={advancedMode}>{obs?.timestamp}</Timestamp>
      </FSTime>
    </FullScreenBanner>
  )
}
FileBanner.propTypes = {
  observationId: PropTypes.string.isRequired,
  file: PropTypes.object.isRequired,
}

export default ObservationMediaFile
